import { useEffect, useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import type { StudioRecipe } from '@sr-test-wizard/shared';
import type { GitLabProjectSearchHit } from '../../lib/studioRecipesApi';
import { KeyValueEditor, pairsToRecord, recordToPairs } from './KeyValueEditor';
import {
  MarketOverridesEditor,
  overridesToRows,
  rowsToOverrides,
  type MarketOverrideRow,
} from './MarketOverridesEditor';
import { GitLabPickerDialog } from './GitLabPickerDialog';
import { TestRenderPanel } from './TestRenderPanel';

interface FormState {
  studio: string;
  version: number;
  playUrlMode: 'full' | 'base' | 'ignore';
  urlTemplate: string;
  playUrlBase: string;
  marketCodePairs: Array<[string, string]>;
  defaultLang: string;
  defaultCurrency: string;
  defaultNotes: string;
  defaultUser: string;
  defaultPassword: string;
  perMarketRows: MarketOverrideRow[];
  launchGuideMd: string;
  /**
   * GitLab project id used by Phase 3 (Pull Source Code). Empty string =
   * "not configured here, fall back to the bundled STUDIOS_INFO map on
   * the server".
   */
  gitlabProjectId: string;
  /** GitLab branch to clone. Defaults to "master" when blank. */
  gitlabBranch: string;
  /** Operator notes injected into AI surfaces. Empty = no extra context. */
  aiKnowledgeMd: string;
}

function fromRecipe(recipe: StudioRecipe): FormState {
  const defaults = recipe.defaults ?? {};
  const credentials = (defaults.credentials ?? {}) as { user?: string | null; password?: string | null };
  return {
    studio: recipe.studio,
    version: recipe.version ?? 1,
    playUrlMode: recipe.playUrlMode,
    urlTemplate: recipe.urlTemplate ?? '',
    playUrlBase: recipe.playUrlBase ?? '',
    marketCodePairs: recordToPairs(recipe.marketCodeMap ?? {}),
    defaultLang: defaults.lang ?? '',
    defaultCurrency: defaults.currency ?? '',
    defaultNotes: defaults.notes ?? '',
    defaultUser: credentials.user ?? '',
    defaultPassword: credentials.password ?? '',
    perMarketRows: overridesToRows(recipe.perMarketOverrides ?? {}),
    launchGuideMd: recipe.launchGuideMd ?? '',
    gitlabProjectId: recipe.gitlab?.projectId ?? '',
    gitlabBranch: recipe.gitlab?.branch ?? '',
    aiKnowledgeMd: recipe.aiKnowledgeMd ?? '',
  };
}

/**
 * Build the validated runtime shape from form state. Empty strings collapse
 * to null/undefined so the recipe matches what `loadRecipe` would parse from
 * disk and we don't emit useless `"": ""` entries.
 */
function toRecipe(state: FormState): StudioRecipe {
  const projectId = state.gitlabProjectId.trim();
  const rawBranch = state.gitlabBranch.trim();
  const branch =
    !rawBranch || rawBranch.toLowerCase() === 'main' ? (rawBranch ? 'master' : '') : rawBranch;
  // Collapse the whole gitlab block to null when the user clears both
  // fields; lets the server fall back to the bundled STUDIOS_INFO map
  // instead of treating an empty record as "explicitly unconfigured".
  const gitlab =
    projectId || branch
      ? {
          projectId: projectId || null,
          branch: branch || (projectId ? 'master' : null),
        }
      : null;
  return {
    studio: state.studio.trim(),
    version: state.version || 1,
    playUrlMode: state.playUrlMode,
    urlTemplate: state.urlTemplate,
    playUrlBase: state.playUrlBase.trim() || undefined,
    marketCodeMap: pairsToRecord(state.marketCodePairs),
    defaults: {
      lang: state.defaultLang.trim() || null,
      currency: state.defaultCurrency.trim() || null,
      notes: state.defaultNotes.trim() || null,
      credentials: {
        user: state.defaultUser.trim() || null,
        password: state.defaultPassword.trim() || null,
      },
    },
    perMarketOverrides: rowsToOverrides(state.perMarketRows),
    launchGuideMd: state.launchGuideMd,
    // source + clientBriefMd are not user-editable in this form; they're
    // managed by the ConfluencePanel. Preserve them as null here and let
    // the parent merge in the persisted values before save().
    source: null,
    clientBriefMd: null,
    aiKnowledgeMd: state.aiKnowledgeMd.trim() || null,
    gitlab,
  };
}

interface Props {
  /** Recipe to edit. The form takes its own copy on mount/recipe-change. */
  recipe: StudioRecipe;
  slug: string;
  source: 'user' | 'bundled';
  /** Whether a user override exists; controls the Delete-vs-Save behaviour. */
  hasUserOverride: boolean;
  saving: boolean;
  saveError: string | null;
  onSave: (recipe: StudioRecipe) => Promise<void>;
  onDelete?: () => Promise<void> | void;
  onClose: () => void;
}

const PLACEHOLDER_HINT =
  'Available: {{playUrl}}, {{playUrlBase}}, {{gameId}}, {{gameName}}, {{market}}, {{marketCode}}, {{lang}}, {{currency}}, {{jurisdiction}}, {{credentials.user}}, {{credentials.password}}, {{notes}} - plus any per-market override key.';

/**
 * Read/write editor for a single `StudioRecipe`. Owns its draft state so the
 * parent only sees a recipe when the user clicks Save - that way switching
 * tabs/recipes or hitting Cancel doesn't half-commit.
 */
export function StudioRecipeForm({
  recipe,
  slug,
  source,
  hasUserOverride,
  saving,
  saveError,
  onSave,
  onDelete,
  onClose,
}: Props) {
  const [state, setState] = useState<FormState>(() => fromRecipe(recipe));
  // Reset whenever the parent feeds us a different recipe (e.g. user picked
  // a different studio in the list, or a fresh draft came back from PDF).
  useEffect(() => {
    setState(fromRecipe(recipe));
  }, [recipe]);

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setState((s) => ({ ...s, [key]: value }));
  };

  const recipeForPreview = useMemo(() => toRecipe(state), [state]);
  const [showGuidePreview, setShowGuidePreview] = useState(false);
  const [showGitLabPicker, setShowGitLabPicker] = useState(false);

  const onGitLabPick = (hit: GitLabProjectSearchHit) => {
    setState((s) => ({
      ...s,
      gitlabProjectId: String(hit.id),
      // Archives are always on master in client-critical-files, even when
      // GitLab reports `main` as the project default branch.
      gitlabBranch: 'master',
    }));
    setShowGitLabPicker(false);
  };

  const submit = async () => {
    await onSave(toRecipe(state));
  };

  const renderSaveButton = () => (
    <button
      type="button"
      className="btn btn-success"
      disabled={saving}
      onClick={() => void submit()}
    >
      {saving ? 'Saving...' : 'SAVE'}
    </button>
  );

  return (
    <div className="flex-col">
      <div className="card">
        <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
          <h2 style={{ margin: 0 }}>{state.studio || 'New studio'}</h2>
          <span className="badge small mono">{slug}</span>
          <span className={`badge small ${source === 'user' ? 'info' : 'grey'}`}>
            {source === 'user' ? 'User override' : 'Bundled seed'}
          </span>
          <span className="spacer" />
          <button type="button" className="btn small" onClick={onClose}>
            Close
          </button>
          {onDelete && hasUserOverride && (
            <button
              type="button"
              className="btn small btn-danger"
              onClick={() => void onDelete()}
              title="Remove the user override; falls back to the bundled seed if one exists."
            >
              Revert / delete override
            </button>
          )}
          {renderSaveButton()}
        </div>
        {saveError && (
          <p style={{ color: 'var(--bad)', marginTop: 8, marginBottom: 0 }}>{saveError}</p>
        )}
      </div>

      <div className="card">
        <h3>Studio</h3>
        <div className="row" style={{ gap: 8 }}>
          <input
            value={state.studio}
            onChange={(e) => update('studio', e.target.value)}
            placeholder="Studio name (e.g. Octoplay)"
            style={{ flex: 1 }}
          />
        </div>
      </div>

      <div className="card">
        <div className="row" style={{ marginBottom: 8, alignItems: 'center' }}>
          <h3 style={{ margin: 0 }}>GitLab project (Phase 3 source code)</h3>
          <span className="spacer" />
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setShowGitLabPicker(true)}
          >
            GIT auto project map
          </button>
        </div>
        <p className="muted small" style={{ margin: '0 0 8px' }}>
          Used by the "Pull Source Code" step to clone the studio's GitLab
          repository. The project id is the numeric id visible in GitLab's
          project page URL (e.g. <code>40</code> for
          <code> gitlab.example.com/-/projects/40</code>). Leave blank to
          fall back to the bundled studio-to-project map on the server. Use
          GIT auto project map to search <code>client-critical-files</code> by
          studio name. Game archives are always on <code>master</code> (not
          <code>main</code>); auto-map fills that branch.
        </p>
        <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
          <label className="muted small" style={{ minWidth: 100 }}>
            Project id
          </label>
          <input
            value={state.gitlabProjectId}
            onChange={(e) => update('gitlabProjectId', e.target.value)}
            placeholder="40"
            inputMode="numeric"
            style={{ width: 160 }}
          />
          <label className="muted small" style={{ minWidth: 60 }}>
            Branch
          </label>
          <input
            value={state.gitlabBranch}
            onChange={(e) => update('gitlabBranch', e.target.value)}
            placeholder="master"
            style={{ width: 200 }}
          />
        </div>
        {showGitLabPicker && (
          <GitLabPickerDialog
            initialQuery={state.studio.trim() || slug}
            onClose={() => setShowGitLabPicker(false)}
            onPick={onGitLabPick}
          />
        )}
      </div>

      <div className="card">
        <h3>URL generation</h3>
        <div className="flex-col" style={{ gap: 8 }}>
          <div className="row" style={{ gap: 12, flexWrap: 'wrap' }}>
            {(['base', 'full', 'ignore'] as const).map((mode) => (
              <label key={mode} className="row" style={{ gap: 6 }}>
                <input
                  type="radio"
                  name="playUrlMode"
                  checked={state.playUrlMode === mode}
                  onChange={() => update('playUrlMode', mode)}
                />
                <span className="mono small">{mode}</span>
              </label>
            ))}
          </div>
          <p className="muted small" style={{ margin: 0 }}>
            <strong>base</strong>: take the Game task's Play URL host as <code>{'{{playUrlBase}}'}</code> and apply the
            template. <strong>full</strong>: use the Game task's Play URL verbatim (template ignored).{' '}
            <strong>ignore</strong>: ignore the Game task; always use the recipe's <code>playUrlBase</code>.
          </p>
          <label className="muted small">Fallback playUrlBase (optional)</label>
          <input
            value={state.playUrlBase}
            onChange={(e) => update('playUrlBase', e.target.value)}
            placeholder="https://lobby.example.com/launch"
          />
          <label className="muted small">URL template</label>
          <textarea
            value={state.urlTemplate}
            onChange={(e) => update('urlTemplate', e.target.value)}
            placeholder="{{playUrlBase}}?token={{token}}&currency={{currency}}&language={{lang}}&brand={{brand}}"
            style={{
              width: '100%',
              minHeight: 80,
              boxSizing: 'border-box',
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
            }}
          />
          <p className="muted small" style={{ margin: 0 }}>{PLACEHOLDER_HINT}</p>
        </div>
      </div>

      <div className="card">
        <h3>Market code map</h3>
        <p className="muted small" style={{ margin: '0 0 8px' }}>
          Maps TEXEC market codes (left) to the URL/brand market codes (right). Used as <code>{'{{marketCode}}'}</code>{' '}
          in templates.
        </p>
        <KeyValueEditor
          pairs={state.marketCodePairs}
          onChange={(p) => update('marketCodePairs', p)}
          keyPlaceholder="TEXEC market (e.g. MGA)"
          valuePlaceholder="URL market code (e.g. mt)"
        />
      </div>

      <div className="card">
        <h3>Defaults</h3>
        <div className="flex-col" style={{ gap: 8 }}>
          <div className="row" style={{ gap: 8 }}>
            <label className="muted small" style={{ minWidth: 100 }}>
              Language
            </label>
            <input value={state.defaultLang} onChange={(e) => update('defaultLang', e.target.value)} placeholder="en" />
            <label className="muted small">Currency</label>
            <input
              value={state.defaultCurrency}
              onChange={(e) => update('defaultCurrency', e.target.value)}
              placeholder="EUR"
            />
          </div>
          <div className="row" style={{ gap: 8 }}>
            <label className="muted small" style={{ minWidth: 100 }}>
              Tester user
            </label>
            <input
              value={state.defaultUser}
              onChange={(e) => update('defaultUser', e.target.value)}
              placeholder="<your tester email>"
              style={{ flex: 1 }}
            />
          </div>
          <div className="row" style={{ gap: 8 }}>
            <label className="muted small" style={{ minWidth: 100 }}>
              Tester password
            </label>
            <input
              value={state.defaultPassword}
              onChange={(e) => update('defaultPassword', e.target.value)}
              placeholder="<password / how to obtain>"
              style={{ flex: 1 }}
            />
          </div>
          <label className="muted small">Notes</label>
          <textarea
            value={state.defaultNotes}
            onChange={(e) => update('defaultNotes', e.target.value)}
            placeholder="One-liner shown in the launch guide as {{notes}}."
            style={{ width: '100%', minHeight: 80, boxSizing: 'border-box' }}
          />
        </div>
      </div>

      <div className="card">
        <h3>Per-market overrides</h3>
        <p className="muted small" style={{ margin: '0 0 8px' }}>
          Anything you put here merges over the defaults for that market and is
          available in the URL/guide as a <code>{'{{key}}'}</code> placeholder.
        </p>
        <MarketOverridesEditor rows={state.perMarketRows} onChange={(rows) => update('perMarketRows', rows)} />
      </div>

      <div className="card">
        <h3>Launch guide (markdown)</h3>
        <p className="muted small" style={{ margin: '0 0 8px' }}>
          Shown to testers under each generated URL. Supports the same{' '}
          <code>{'{{placeholders}}'}</code> as the URL template.
        </p>
        <textarea
          value={state.launchGuideMd}
          onChange={(e) => update('launchGuideMd', e.target.value)}
          style={{
            width: '100%',
            minHeight: 220,
            boxSizing: 'border-box',
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
          }}
        />
        <div className="row" style={{ marginTop: 8 }}>
          <button
            type="button"
            className="btn small btn-ghost"
            onClick={() => setShowGuidePreview((s) => !s)}
          >
            {showGuidePreview ? 'Hide preview' : 'Show preview'}
          </button>
        </div>
        {showGuidePreview && (
          <div className="env-link-guide" style={{ marginTop: 10 }}>
            <ReactMarkdown>{state.launchGuideMd}</ReactMarkdown>
          </div>
        )}
      </div>

      <div className="card">
        <h3>AI knowledge</h3>
        <p className="muted small" style={{ margin: '0 0 8px' }}>
          Optional notes for Look at that, Phase 7, bug chat, and video
          analysis. Use this for studio chrome the bot should not relearn
          every game: HUD / hamburger, History vs Autoplay circular-arrows,
          debug or cheat menus, popups. Leave blank for no extra context.
          This is not the Confluence wiki brief (that stays auto-synced).
        </p>
        <textarea
          value={state.aiKnowledgeMd}
          onChange={(e) => update('aiKnowledgeMd', e.target.value)}
          placeholder="History is in the hamburger, not the circular-arrows Autoplay control. Debug menu: …"
          style={{
            width: '100%',
            minHeight: 160,
            boxSizing: 'border-box',
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
          }}
        />
      </div>

      <TestRenderPanel recipe={recipeForPreview} />

      <div className="card">
        <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
          {saveError && (
            <p style={{ color: 'var(--bad)', margin: 0, flex: 1 }}>{saveError}</p>
          )}
          <span className="spacer" />
          <button type="button" className="btn small" onClick={onClose}>
            Close
          </button>
          {renderSaveButton()}
        </div>
      </div>
    </div>
  );
}
